import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  type ChartOptions,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
);

/** DaisyUI CSS değişkenini okur (tema değişimlerinde güncel değer döner) */
function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** data-theme mutasyonunu izleyerek React state'ini günceller */
function useTheme() {
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute('data-theme') ?? 'light',
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute('data-theme') ?? 'light');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

// ── Asset renkleri ───────────────────────────────────────────────────────────
const ASSET_COLORS: Record<string, string> = {
  crypto: '#f59e0b',
  equity: '#3b82f6',
  etf: '#10b981',
  cash: '#6b7280',
};

// ── Exported types ───────────────────────────────────────────────────────────
export interface ChartDataPoint {
  month: string;
  value: number;
}

export interface AssetPieDataPoint {
  name: string;
  value: number;
}

// ── PortfolioAreaChart ───────────────────────────────────────────────────────
interface PortfolioAreaChartProps {
  data: ChartDataPoint[];
}

export function PortfolioAreaChart({ data }: PortfolioAreaChartProps) {
  const { _ } = useLingui();
  const theme = useTheme();

  const primaryColor = getCSSVar('--color-primary');
  const mutedColor = getCSSVar('--color-base-content');
  const gridColor = getCSSVar('--color-base-300');

  const chartData = {
    labels: data.map((d) => d.month),
    datasets: [
      {
        data: data.map((d) => d.value),
        fill: true,
        borderColor: primaryColor,
        backgroundColor: `color-mix(in oklch, ${primaryColor} 20%, transparent)`,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: primaryColor,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: getCSSVar('--color-base-100'),
        borderColor: gridColor,
        borderWidth: 1,
        titleColor: mutedColor,
        bodyColor: primaryColor,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => (ctx.parsed.y ?? 0).toLocaleString('tr-TR'),
        },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: mutedColor, font: { size: 11 } },
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: mutedColor, font: { size: 11 } },
      },
    },
  };

  return (
    <div className="card bg-base-100 shadow border border-base-300">
      <div className="card-body">
        <h2 className="card-title text-base">{_({ id: 'dashboard.areaChart.title' })}</h2>
        {data.length > 0 ? (
          <div className="h-64">
            {/* key={theme} — tema değişince Chart.js'i yeniden monte et */}
            <Line key={theme} data={chartData} options={options} />
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-base-content/40 text-sm">
            {_({ id: 'dashboard.areaChart.empty' })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── AssetDistributionChart ───────────────────────────────────────────────────
interface AssetDistributionChartProps {
  data: AssetPieDataPoint[];
}

export function AssetDistributionChart({ data }: AssetDistributionChartProps) {
  const { _ } = useLingui();
  const theme = useTheme();

  const borderColor = getCSSVar('--color-base-100');
  const mutedColor = getCSSVar('--color-base-content');

  const chartData = {
    labels: data.map((d) => d.name),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => ASSET_COLORS[d.name] ?? '#8b5cf6'),
        borderColor,
        borderWidth: 2,
        hoverOffset: 6,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: mutedColor,
          font: { size: 12 },
          padding: 16,
          usePointStyle: true,
          pointStyleWidth: 10,
        },
      },
      tooltip: {
        backgroundColor: getCSSVar('--color-base-100'),
        borderColor: getCSSVar('--color-base-300'),
        borderWidth: 1,
        titleColor: mutedColor,
        bodyColor: mutedColor,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${String(ctx.label)}: ${String(ctx.parsed)}`,
        },
      },
    },
  };

  return (
    <div className="card bg-base-100 shadow border border-base-300">
      <div className="card-body">
        <h2 className="card-title text-base">{_({ id: 'dashboard.pieChart.title' })}</h2>
        {data.length > 0 ? (
          <div className="h-52">
            <Doughnut key={theme} data={chartData} options={options} />
          </div>
        ) : (
          <div className="h-52 flex items-center justify-center text-base-content/50 text-sm">
            {_({ id: 'dashboard.pieChart.empty' })}
          </div>
        )}
      </div>
    </div>
  );
}

export interface ChartDataPoint {
  month: string;
  value: number;
}
